// /verify (docs/08-app-design.md 5.10): a static page anyone can check, with
// no unlock. Every fact is shown next to the command that reproduces it, and
// every RPC failure becomes a StatusLine rather than a blank page (section 2,
// "fail calmly, never silently"). The deployed program hash is computed in
// this browser, from the ProgramData account, and compared against the
// published one; against the local rpc shim the program account is not in
// the upgradeable-loader layout, so there is nothing to compare and the page
// says so instead of erroring.

import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { FEE_BPS, treasuryAddress } from '@thevault/sdk';
import * as canary from '@thevault/sdk/canary';
import { Kv, StatusLine } from '../components/primitives';
import { Shell } from '../components/Shell';
import { hexOf } from '../engine';
import { PUBLISHED_ARTIFACT_LEN, PUBLISHED_SHA256 } from '../program';
import { PROGRAM_ID, PROGRAM_ID_B58, session, useSession } from '../session';
import { toBase58, type VersionInfo } from '../rpc';

// The source repository is private until the external audit, so the page links to the documentation's status section instead.

// Published facts live in src/program.ts (one source for this page and for
// the engine's refusal to sign against a program that differs).

interface LoadResult {
  version: VersionInfo | null;
  versionError: string | null;
  programOwner: string | null;
  programError: string | null;
  /** True only when the program account is a `Program` account (tag 2) owned by the upgradeable BPF loader: the layout the deployed hash is read from. */
  loaderApplies: boolean;
  programDataAddress: string | null;
  elfSha256: string | null;
  tailIsZero: boolean | null;
  hashError: string | null;
  treasuryAddr: string | null;
  treasuryError: string | null;
}

async function load(): Promise<LoadResult> {
  const rpc = session.rpc();
  const out: LoadResult = {
    version: null,
    versionError: null,
    programOwner: null,
    programError: null,
    loaderApplies: false,
    programDataAddress: null,
    elfSha256: null,
    tailIsZero: null,
    hashError: null,
    treasuryAddr: null,
    treasuryError: null,
  };

  try {
    out.version = await rpc.getVersion();
  } catch (e) {
    out.versionError = e instanceof Error ? e.message : String(e);
  }

  try {
    const account = await rpc.getAccountInfo(PROGRAM_ID_B58);
    if (!account) {
      out.programError = 'The program id does not exist on this cluster.';
    } else {
      out.programOwner = toBase58(account.owner);
      const tag = account.data.length >= 4 ? new DataView(account.data.buffer, account.data.byteOffset, account.data.byteLength).getUint32(0, true) : -1;
      out.loaderApplies = out.programOwner === canary.UPGRADEABLE_LOADER_ADDRESS && tag === 2;
      if (out.loaderApplies) {
        out.programDataAddress = toBase58(account.data.slice(4, 36));
        try {
          const pd = await rpc.getAccountInfo(out.programDataAddress);
          if (!pd) {
            out.hashError = 'The ProgramData account was not found.';
          } else {
            const elfRegion = pd.data.slice(45);
            if (elfRegion.length < PUBLISHED_ARTIFACT_LEN) {
              out.hashError = `The ProgramData ELF region is ${elfRegion.length} bytes, shorter than the published artifact (${PUBLISHED_ARTIFACT_LEN}).`;
            } else {
              const digest = await crypto.subtle.digest('SHA-256', elfRegion.slice(0, PUBLISHED_ARTIFACT_LEN));
              out.elfSha256 = hexOf(new Uint8Array(digest));
              out.tailIsZero = elfRegion.slice(PUBLISHED_ARTIFACT_LEN).every((b) => b === 0);
            }
          }
        } catch (e) {
          out.hashError = e instanceof Error ? e.message : String(e);
        }
      }
    }
  } catch (e) {
    out.programError = e instanceof Error ? e.message : String(e);
  }

  try {
    out.treasuryAddr = toBase58(await treasuryAddress(PROGRAM_ID));
  } catch (e) {
    out.treasuryError = e instanceof Error ? e.message : String(e);
  }

  return out;
}

export function Verify() {
  const { state, settings } = useSession();
  const [data, setData] = useState<LoadResult | null>(null);

  useEffect(() => {
    if (state === 'starting') return;
    let cancelled = false;
    setData(null);
    void load().then((d) => {
      if (!cancelled) setData(d);
    });
    return () => {
      cancelled = true;
    };
    // settings.rpcUrl is read inside load() via session.rpc(); re-run when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, settings.rpcUrl]);

  const hashMatches = data?.elfSha256 === PUBLISHED_SHA256 && data?.tailIsZero === true;

  return (
    <Shell number="07" title="Verify" detail="NO UNLOCK NEEDED">
      <h1>Verify the deployment.</h1>
      <p className="lede">Everything below is a fact this device reads itself, next to the command that reproduces it. Nothing here needs a signature.</p>

      <section className="panel" aria-labelledby="verify-program-title">
        <div className="console-heading">
          <h3 id="verify-program-title">Program</h3>
        </div>
        <Kv
          rows={[
            ['Program id', <span className="mono">{PROGRAM_ID_B58}</span>],
            ['ProgramData address', !data ? 'reading…' : data.programDataAddress ? <span className="mono">{data.programDataAddress}</span> : '—'],
          ]}
        />
        {data?.programError ? (
          <StatusLine state="failed" word="Could not read the program account.">
            {data.programError}
          </StatusLine>
        ) : null}
        {data && !data.programError && !data.loaderApplies ? (
          <StatusLine state="neutral" word="Nothing to compare.">
            This cluster does not use the upgradeable loader; there is nothing to compare.
          </StatusLine>
        ) : null}

        {data?.loaderApplies ? (
          <>
            <Kv
              rows={[
                ['Published hash', <span className="mono">{PUBLISHED_SHA256}</span>],
                ['Deployed hash', data.hashError ? '—' : <span className="mono">{data.elfSha256}</span>],
                ['Artifact length', `${PUBLISHED_ARTIFACT_LEN.toLocaleString('en-US')} bytes`],
                ['Bytes beyond the artifact', data.tailIsZero === null ? 'not checked' : data.tailIsZero ? 'all zero' : 'non-zero, unexplained'],
              ]}
            />
            {data.hashError ? (
              <StatusLine state="failed" word="Could not compute the deployed hash.">
                {data.hashError}
              </StatusLine>
            ) : hashMatches ? (
              <StatusLine state="landed" word="MATCHES">
                The deployed program hash equals the published hash, and the ProgramData account carries no bytes beyond it.
              </StatusLine>
            ) : (
              <StatusLine state="failed" word="DOES NOT MATCH">
                The deployed program does not equal the published artifact.
              </StatusLine>
            )}
          </>
        ) : null}
        <p className="source-label">Reproduce from a shell, against mainnet.</p>
        <pre className="code">
          <code>{`solana program dump ${PROGRAM_ID_B58} thevault-deployed.so -u mainnet-beta`}</code>
        </pre>
        <pre className="code">
          <code>{`head -c ${PUBLISHED_ARTIFACT_LEN} thevault-deployed.so | sha256sum`}</code>
        </pre>
        <pre className="code">
          <code>{`tail -c +${PUBLISHED_ARTIFACT_LEN + 1} thevault-deployed.so | tr -d '\\000' | wc -c`}</code>
        </pre>
        <p className="source-label">
          The dump holds the whole ProgramData region, which is larger than the program; the first command hashes the program's bytes, and the second prints 0 when every byte after them is zero.
        </p>
      </section>

      <section className="panel" aria-labelledby="verify-treasury-title">
        <div className="console-heading">
          <h3 id="verify-treasury-title">Treasury fee</h3>
        </div>
        <Kv
          rows={[
            ['Fee rate', `${(FEE_BPS / 100).toFixed(2)} % of each swap`],
            // program/src/fee.rs `fee_side`: decided by the two mints the signature binds.
            ['Paid in', 'USDC or USDT when either side is one (the output when both are), otherwise SOL when either side is SOL, otherwise the output token'],
            ['Treasury address', !data ? 'reading…' : data.treasuryAddr ? <span className="mono">{data.treasuryAddr}</span> : '—'],
            ['Expected treasury address', <span className="mono">{canary.EXPECTED_TREASURY_ADDRESS}</span>],
          ]}
        />
        {data?.treasuryError ? (
          <StatusLine state="failed" word="Could not derive the treasury address.">
            {data.treasuryError}
          </StatusLine>
        ) : data ? (
          data.treasuryAddr === canary.EXPECTED_TREASURY_ADDRESS ? (
            <StatusLine state="landed" word="MATCHES">
              The treasury address derived here equals the published address.
            </StatusLine>
          ) : (
            <StatusLine state="failed" word="DOES NOT MATCH">
              The treasury address derived here does not equal the published address.
            </StatusLine>
          )
        ) : null}
      </section>

      <section className="panel" aria-labelledby="verify-rpc-title">
        <div className="console-heading">
          <h3 id="verify-rpc-title">RPC and build</h3>
        </div>
        <Kv
          rows={[
            ['RPC endpoint', <span className="mono">{settings.rpcUrl}</span>],
            ['getVersion', !data ? 'reading…' : (data.versionError ?? data.version?.['solana-core'] ?? '—')],
            [
              'This build',
              <span className="mono">
                {__GIT_COMMIT__}
                {__GIT_DIRTY__ ? ', with uncommitted changes' : ', clean tree'}
              </span>,
            ],
            ['Lockfile sha256', <span className="mono">{__LOCKFILE_SHA256__}</span>],
          ]}
        />
        {data?.versionError ? (
          <StatusLine state="failed" word="Could not reach the RPC endpoint.">
            {data.versionError}
          </StatusLine>
        ) : null}
      </section>

      <section className="panel" aria-labelledby="verify-deps-title">
        <div className="console-heading">
          <h3 id="verify-deps-title">Dependencies</h3>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Version</th>
            </tr>
          </thead>
          <tbody>
            {(JSON.parse(__DEPENDENCIES__) as Array<{ name: string; version: string }>).map((d) => (
              <tr key={d.name}>
                <td>{d.name}</td>
                <td className="mono">{d.version}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="source-label">Reproduce from a shell, in this repository.</p>
        <pre className="code">
          <code>cd app && npm run build</code>
        </pre>
        <p>The hash of the built files is served by this site at /BUILD-HASH.txt; a page cannot show its own hash. Rebuilding it needs the source, which is published with the audit.</p>
      </section>

      <section className="panel" aria-labelledby="verify-links-title">
        <div className="console-heading">
          <h3 id="verify-links-title">Links</h3>
        </div>
        <p>The source code, the review records and the evidence files are private until the external audit, and will be published with the audit reports.</p>
        <ul className="link-list">
          <li>
            <Link href="/protection#reviews">Where the project stands</Link>
          </li>
          <li>
            <Link href="/protection">How protection works</Link>
          </li>
        </ul>
      </section>
    </Shell>
  );
}
